import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  IconButton,
  Link,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { Link as RouterLink } from "react-router";
import { plural } from "../../program-access/access-model";
import { listedContributors } from "../../program-access/components/access-dialog";
import { programHref } from "../../program-tree/components/program-switcher";
import { ProgramIcon } from "../../programs/components/program-icon";
import { matchText } from "../program-landing";

import type { AccessData } from "../../program-access/hooks/use-program-access";
import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { FC, ReactElement, ReactNode } from "react";

/** The band of the design behind the top of a program's page. */
export const BAND =
  "radial-gradient(446.22% 109.48% at 50% 0%, rgba(177, 79, 150, 0.1) 0%, rgba(35, 181, 235, 0.15) 50%, rgba(255, 255, 255, 0) 100%)";

/** A tint of a program's colour, which can be in any CSS form. */
export const tint = (colour: string): string => `color-mix(in srgb, ${colour} 8%, transparent)`;

/** Read by a screen reader, but not shown. */
export const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
} as const;

/** A count in a tile, such as the number of drafts to finish. */
export const Tile: FC<{ label: string; value: number; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <Card
    sx={{
      px: 2,
      pt: 1,
      pb: 1.5,
      borderRadius: "4px",
      boxShadow: "0 1px 6px rgba(100, 116, 139, 0.12)",
    }}
  >
    <Typography variant="overline" component="p" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="h5" component="p" sx={{ color }}>
      {value}
    </Typography>
  </Card>
);

/** Above the title link, which covers its card or row, so that a click on it does not open the program. */
const ABOVE_LINK = { position: "relative", zIndex: 1 } as const;

/**
 * The program's title, which opens it. The link covers the nearest positioned card or row, so a
 * click anywhere on it opens the program, and the link's name is only the title.
 */
export const ProgramTitleLink: FC<{ node: ProgramNode; children?: ReactNode }> = ({
  node,
  children,
}) => (
  <Link
    component={RouterLink}
    to={programHref(node.id)}
    underline="none"
    color="inherit"
    className="program-title"
    sx={{ "&::after": { content: '""', position: "absolute", inset: 0 } }}
  >
    {children ?? node.title}
  </Link>
);

/**
 * The program's icon in a circle of its colour, or the icon given in its place. A raised one has a
 * white edge and a shadow.
 */
export const ProgramBadge: FC<{
  node: Pick<ProgramNode, "theme_colour" | "icon_value">;
  size: number;
  raised?: boolean;
  icon?: ReactElement;
}> = ({ node, size, raised = false, icon }) => (
  <Box
    aria-hidden
    sx={{
      ...(raised && { border: 4, borderColor: "common.white", boxShadow: 1 }),
      boxSizing: "border-box",
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: node.theme_colour,
      bgcolor: tint(node.theme_colour),
    }}
  >
    <ProgramIcon data={icon ?? node.icon_value} sx={{ fontSize: size / 2 }} />
  </Box>
);

/** Stars or unstars the program, to keep it at the top of the page. */
export const FavouriteButton: FC<{
  node: ProgramNode;
  favourite: boolean;
  onToggle: () => void;
}> = ({ node, favourite, onToggle }) => (
  <IconButton
    onClick={onToggle}
    aria-pressed={favourite}
    aria-label={
      favourite ? `Remove ${node.title} from favourites` : `Add ${node.title} to favourites`
    }
    title={favourite ? "Remove from favourites" : "Add to favourites"}
    sx={{ ...ABOVE_LINK, color: favourite ? "warning.dark" : "text.disabled" }}
    data-analytics-id="programs-landing-favourite"
  >
    {favourite ? <StarIcon /> : <StarBorderIcon />}
  </IconButton>
);

/** "Folder: A, B +3", the folders that match the search. */
export const MatchBadge: FC<{ folders: string[] }> = ({ folders }) => (
  <Box
    sx={{
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      height: 26,
      pl: 1,
      pr: 1.25,
      borderRadius: 1,
      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
      color: "warning.dark",
      typography: "caption",
      fontWeight: 600,
    }}
  >
    <FolderOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
    {matchText(folders)}
  </Box>
);

/**
 * Who can see the program: the admins, and the contributors listed in its Access dialog, with the
 * first contributors' avatars unless avatars is false.
 */
export const AccessPill: FC<{
  data: AccessData;
  tree: ProgramTree;
  node: ProgramNode;
  avatars?: boolean;
}> = ({ data, tree, node, avatars = true }) => {
  const listed = listedContributors(data, tree, node);
  const contributors = plural(listed.length, "contributor");

  return (
    <Tooltip
      title={
        listed.length
          ? `Admins and ${contributors} can see this program`
          : "Only admins can see this program"
      }
    >
      <Box
        sx={{
          ...ABOVE_LINK,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: 26,
          pl: 1,
          pr: 1.25,
          borderRadius: 13,
          bgcolor: "action.hover",
          typography: "caption",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 15 }} />
        {avatars && listed.length ? (
          <AvatarGroup max={3} sx={{ "& .MuiAvatar-root": { width: 20, height: 20, fontSize: 9 } }}>
            {listed.map(({ person }) => (
              <Avatar key={person.id}>{person.initials}</Avatar>
            ))}
          </AvatarGroup>
        ) : null}
        {listed.length ? contributors : "Admins only"}
      </Box>
    </Tooltip>
  );
};
